import React, { useCallback, useEffect, useState } from "react";

import { Cancel } from "@mui/icons-material";
import Loading from "components/Loading";
import { US_STATE } from "utils/config/reference";
import { map } from "lodash";
import PhraseLoader from "./PhraseLoader";

import {
  Autocomplete,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Input,
} from "@mui/material";
import { useAlert } from "components/UI/alertSakeBar/alertProvider";
import { makeStyles } from "@mui/styles";
import { levelsData } from "components/StoryWithAi/constants";
import { muiStyled } from "utils/styles/mui";
import styled from "styled-components";
import usePopUpAtOnce from "components/hooks/usePopUpAtOnce";
import SentryWrap from 'components/Sentry';
import { logToSentry } from "utils/networkErrorUtils";

const timeMenuItems = [
  { value: 25, label: "25 min" },
  { value: 35, label: "35 min" },
  { value: 45, label: "45 min" },
  { value: 55, label: "55 min" },
  { value: 70, label: "1 hour and 10 min" },
  { value: 80, label: "1 hour and 20 min" },
  { value: 105, label: "1 hour and 45 min" },
  { value: "", label: "custom" },
];

const standardSelection = [
  {
    value: 10,
    label: "American Council on the Teaching of Foreign Languages (ACTFL) Standards",
  },
  {
    value: 20,
    label: "California World Language Standards",
  },
  {
    value: 30,
    label: "Colorado Academic Standards for World Languages",
  },
  {
    value: 40,
    label: "Colorado World Languages Academic Standards",
  },
  {
    value: 50,
    label: "Common Core State Standards",
  },
  {
    value: 60,
    label: "Common Core State Standards (Spanish)",
  },
  {
    value: 70,
    label: "Georgia Standards of Excellence for Modern Languages",
  },
  {
    value: 80,
    label: "Georgia Standards of Excellence for World Languages",
  },
  {
    value: 90,
    label: "Illinois Learning Standards for World Languages",
  },
  {
    value: 100,
    label: "Indiana Academic Standards for World Languages",
  },
  {
    value: 110,
    label: "Kansas World Language Standards (2017)",
  },
  {
    value: 120,
    label: "Kentucky World Language Standards",
  },
  {
    value: 130,
    label: "Louisiana World Language Content Standards",
  },
  {
    value: 140,
    label: "Maine Learning Results for World Languages",
  },
  {
    value: 150,
    label: "Maryland World Language Standards",
  },
  {
    value: 160,
    label: "Massachusetts World Languages Curriculum Framework",
  },
  {
    value: 170,
    label: "Nebraska World Language Standards",
  },
  {
    value: 180,
    label: "Nevada Academic Content Standards for World Languages",
  },
  {
    value: 190,
    label: "New Jersey Student Learning Standards for World Languages",
  },
  {
    value: 200,
    label: "New Mexico World-Readiness Standards for Learning Languages",
  },
  {
    value: 210,
    label: "New York Next Generation State Standards",
  },
  {
    value: 220,
    label: "New York State Learning Standards for Languages Other Than English",
  },
  {
    value: 230,
    label: "Next Generation Sunshine State Standards for World Languages",
  },
  {
    value: 240,
    label: "National Dual Language Standards",
  },
  {
    value: 250,
    label: "Oklahoma Academic Standards for World Languages (2022)",
  },
  {
    value: 260,
    label: "Oregon World Language Standards",
  },
  {
    value: 270,
    label: "South Dakota World Languages Standards",
  },
  {
    value: 280,
    label: "TEKS ELAR",
  },
  {
    value: 290,
    label: "TEKS SLAR",
  },
  {
    value: 300,
    label: "Washington State K-12 World Languages Learning Standards",
  },
  {
    value: 310,
    label: "WIDA Standards",
  },
];

const FieldContainer = styled.div`
  label {
    width: 150px;
    display: flex;
    align-items: center;
    margin-bottom: 20px;
  }
  .MuiFormControl-fullWidth {
    border-radius: ${({ borderRadius }) => borderRadius || '30px'};
  }
`;

const activitiesSelection = [
  { value: 10, label: "Art Project" },
  { value: 20, label: "Article Writing" },
  { value: 30, label: "Creation of mental maps" },
  { value: 40, label: "Email Response" },
  { value: 50, label: "Essay Writing" },
  { value: 60, label: "Group Activities" },
  { value: 70, label: "Interactive activities" },
  { value: 80, label: "Listening Activity" },
  { value: 90, label: "Partner Dialogues" },
  { value: 100, label: "Presentation Activity" },
  { value: 110, label: "Reading" },
  { value: 120, label: "Research" },
];


const gradeLevelData = [
  { value: 6, label: "6th" },
  { value: 7, label: "7th" },
  { value: 8, label: "8th" },
  { value: 9, label: "9th" },
  { value: 10, label: "10th" },
  { value: 11, label: "11th" },
  { value: 12, label: "12th" },
];

const useStyles = makeStyles({
  select: {
    "& .MuiSelect-select:focus": {
      backgroundColor: "transparent !important",
    },
    menuItemText: {
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      maxWidth: "150px",
      display: "block",
    },
    disabledButton: {
      color: "white",
      "&.Mui-disabled": {
        color: "white !important",
      },
    },
  },
});

const StyledAutocomplete = muiStyled(Autocomplete)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    padding: 0,
    border: 'none',
    '& fieldset': {
      display: 'none',
    },
  },
  '& .MuiInputBase-root::before, & .MuiInputBase-root::after': {
    display: 'none !important',
  },
  '& .MuiAutocomplete-listbox': {
    display: 'flex',
    flexDirection: 'row',
    overflowX: 'auto',
    maxWidth: '100%',
    whiteSpace: 'nowrap',
  },
  '& .MuiAutocomplete-tag': {
    marginRight: '5px',
    marginBottom: '0',
  },
}));

const TwoFiledContainer = styled.div`
    display: flex;
    .MuiFormControl-fullWidth{
      margin-left:10px;
      width: ${({ width }) => width || '77%'};
      border-radius:${({ borderRadius }) => borderRadius || '30px'};
    }
      .PrivateSwitchBase-root-82{
        padding:0px;
      }
`;

const CreateLessonModal = ({
  content,
  open,
  onClose,
  loader,
  onCreate = async () => {},
  from,
  isCanvasUser,
  page,
  data,
  chapter,
  is_balcon,
  superLessonConfig = {}
}) => {
  const showAlert = useAlert();
  const classes = useStyles();
  const [gradeLevel, setGradeLevel] = useState("");
  const [state, setState] = useState("");
  const [timeFrame, setTimeFrame] = useState("");
  const [days, setDays] = useState("");
  const [language, setLanguage] = useState("");
  const [proficiencyLevel,setProficiencyLevel]=useState([]);
  const [standard, setStandard] = useState([]);
  const [activities, setActivites] = useState([]);
  const [openDropDown, setOpenDropDown] = useState({
    gradeLevel: false,
    state: false,
    timeFrame: false,
    from_page: false,
    to_page: false,
    days: false,
    standard: false,
    activities: false,
    language: false,
    profiency: false
  });
  const [showCustomTime, setShowCustomTime] = useState(false);
  const [ownVocab, setOwnVocab] = useState('');
  const [vocabInput, setVocabInput] = useState('');
  const [fromPageIndex, setFromPageIndex] = useState(0);
  const [selectableContent, setSelectableContent] = useState([]);
  const [toPageId, setToPageId] = useState(null);
  const [fromPage, setFromPage] = useState();
  const [selectedFromPage, setSelectedFromPage] = useState("");
  const [selectedToPage, setSelectedToPage] = useState("");
  const [selectedFromPageId, setSelectedFromPageId] = useState("");
  const [selectedToPageId, setSelectedToPageId] = useState("");
  const currentTokens = React.useMemo(
    () =>
      ownVocab
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    [ownVocab],
  );
  const handleCustomTimeClick = () => {
    setShowCustomTime(true);
  };
  const [hours, setHours] = useState("");
  const [minutes, setMinutes] = useState("");
  const [minuteError, setMinuteError] = useState("");
  const {updatePopUpAtOnce} = usePopUpAtOnce();
  const getSelectableContent = useCallback(() => {
    try {
    if (page && ((data && data.content) || Array.isArray(content)) && chapter) {
      const contents = Array.isArray(content) ? content : data.content || [];
      const contentWithInformate = contents.map((k, i) => ({ ...k, page_dynamic_id: i + 1 }));
      setSelectableContent(
        contents.map((k, i) => ({ ...k, page_dynamic_id: i + 1 }))
      );
      setFromPage(page);
      setToPageId(null);
      let selectedIndex = 0;
        let selectedFromIndex = contents.findIndex((bk) => bk.pageId == superLessonConfig.from);
        let selectedToIndex = contents.findIndex((bk) => bk.pageId == superLessonConfig.to);
        if(selectedFromIndex == -1){
          selectedFromIndex = contentWithInformate.findIndex((bk) => bk.page_dynamic_id == superLessonConfig.from);
        }
        if(selectedToIndex == -1){
          selectedToIndex = contentWithInformate.findIndex((bk) => bk.page_dynamic_id == superLessonConfig.to);
        }
      if (selectedIndex >= 0) {
        const info = contentWithInformate[selectedIndex];
        if(info){
          setSelectedToPage(contentWithInformate[selectedToIndex]?.numberMasked);
          setSelectedFromPage(contentWithInformate[selectedFromIndex]?.numberMasked);
          setFromPageIndex(selectedFromIndex);
        }
      }
    }
  } catch (error) {
    logToSentry({
      message: 'Error getting selectable content',
      error,
      module: 'SUPER_LESSON',
      operation: 'GET_SELECTABLE_CONTENT'
    })
  }
  }, [data, page, chapter, content, open]);
  useEffect(() => {
    getSelectableContent();
  }, [getSelectableContent,data, page, chapter, content, open]);

  const handleToPageChange = (event) => {
    const selectedPageValue = event.target.value;
    const selectedIndex = selectableContent.findIndex(
      (bk) => bk.numberMasked == selectedPageValue
    );
    setSelectedToPageId(selectedIndex);
    if (selectedIndex >= 0) {
      const selectedPageId = selectableContent[selectedIndex].pageId;
      if (parseInt(selectedPageId) - parseInt(fromPage) > 9) {
        showAlert(`Maximum 10 pages can be select.`);
      } else {
        setToPageId(selectedPageId);
        setSelectedToPage(event.target.value);
      }
    }
  };

  const handleFromPageChange = (event) => {
    const selectedPageValue = event.target.value;
    const selectedIndex = selectableContent.findIndex(
      (bk) => bk.numberMasked == selectedPageValue
    );
    setSelectedFromPageId(selectedIndex);
    // if (selectedIndex >= 0) {
    //   const selectedPageId = selectableContent[selectedIndex].pageId;
    //   if (parseInt(toPageId) - parseInt(selectedPageId) > 2) {
    //     showAlert(`Maximum 3 pages can be select.`);
    //     return;
    //   }
    // }
    if (selectableContent[selectedIndex]) {
      setFromPage(selectableContent[selectedIndex].pageId);
    }
    if (selectableContent[selectedIndex].pageId > toPageId) {
      setToPageId(null);
      setSelectedToPage("");
    }
    setSelectedFromPage(selectedPageValue);
    setFromPageIndex(selectedIndex);
    setSelectedToPage("");
    setToPageId(null);
  };
  const handleHoursChange = (e) => {
    const value = e.target.value;
    setHours(value);
  };
  const handleMinutesChange = (e) => {
    const value = e.target.value;
    setMinutes(value);
  };
  const onCustomTime = () => {
    const validHours = Number(hours) || 0;
    const validMinutes = Number(minutes) || 0;
    const totalMinutes = validHours * 60 + validMinutes;
    if (totalMinutes > 150 || totalMinutes < 10) {
      setMinuteError("Time must be between 10 mins and 2h 30m");
    } else {
      setTimeFrame(totalMinutes);
      setMinuteError("");
      handleMenuClose("timeFrame");
    }
  };
  const handleCancelClick = () => {
    setShowCustomTime(false);
    setHours("");
    setMinutes("");
    setMinuteError("");
    setTimeFrame("");
    setSelectedFromPageId("");
    setSelectedToPageId("");
    setSelectedFromPage("");
    setSelectedToPage("");
  };
  const updateState = (name, value) => {
    setOpenDropDown((prev) => ({ ...prev, [name]: value }));
  };

  const handleGradeLevelChange = (event) => {
    setGradeLevel(event.target.value);
  };

  const handleStateChange = (event) => {
    setState(event.target.value);
  };

  const handleTimeFrameChange = (event) => {
    setTimeFrame(event.target.value);
  };

  const handleDaysChange = (event) => {
    setDays(event.target.value);
  };

  const handleStandardChange = (event) => {
    setStandard(event.target.value);
  };

  const handleActivitiesChange = (event) => {
    setActivites(event.target.value);
  };

  const clearAllState = () => {
    setGradeLevel("");
    setState("");
    setTimeFrame("");
    setDays("");
    setStandard([]);
    setActivites([]);
    setLanguage("");
    setProficiencyLevel([]);
    setOwnVocab('');
    setVocabInput('');
    setSelectedFromPageId("");
    setSelectedToPageId("");
    setSelectedFromPage("");
    setSelectedToPage("");
  };

  const handleCreateLesson = async () => {
    updatePopUpAtOnce(true);
    const payload = {
      days,
      state,
      standard: standard.join(", "),
      activities: activities.join(", "),
      grade_level: gradeLevel,
      time_per_lesson: timeFrame,
      language,
      proficiencyLevel: proficiencyLevel.join(", "),
      vocab: ownVocab,
      from_page: selectedFromPageId,
      to_page: selectedToPageId,
    };
    await onCreate(payload);
    clearAllState();
  };

  const handleMenuOpen = (value) => {
    updateState(value, true);
  };

  const handleMenuClose = (value) => {
    updateState(value, false);
  };

  const handleScroll = () => {
    const openKeys = Object.keys(openDropDown).filter(
      (key) => openDropDown[key]
    );
    if (openKeys.length) {
      openKeys.forEach((key) => handleMenuClose(key));
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [openDropDown]);

  const renderSelectedValues = (selected) => {
    if (selected.length === 0) {
      return <span>Select</span>;
    }

    // Display up to 3 selected items directly, others in a tooltip
    const displayedItems = selected.slice(0, 1).map((value) => (
      <span key={value} className={classes.menuItemText}>
        {value}
      </span>
    ));

    const tooltipItems = selected.slice(1).map((value) => value);

    return (
      <Tooltip title={tooltipItems.join(", ")} placement="top">
        <div>
          {displayedItems}
          {selected.length > 1 && (
            <span className={classes.menuItemText}>...</span>
          )}
        </div>
      </Tooltip>
    );
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
   const commitTokens = (parts) => {
      const cleaned = parts.map(s => s.trim()).filter(Boolean);
      if (!cleaned.length) return;
      const merged = Array.from(new Set([...currentTokens, ...cleaned]));
      setOwnVocab(merged.join(","));
    };
  

  return (
    <SentryWrap>
      {open && (
        <div
          className={
            isCanvasUser ? "canvasuperLessionModal" : "superLessionModal"
          }
        >
          <div
            className={`createMyLession ${
              from !== "ebook" ? "create-lesson-modal" : "create-lesson-modals"
            } ${loader ? "createMyLessonLoader" : ""}`}
          >
            {loader ? (
              <div className="super-lesson-waiting">
                <PhraseLoader />{" "}
              </div>
            ) : (
              <>
                <span
                  onClick={() => {
                    handleCancelClick();
                    onClose();
                  }}
                >
                  <Cancel
                    style={{
                      position: "absolute",
                      right: -4,
                      top: -10,
                      fontSize: 22,
                      color: "red",
                      background: "white",
                      borderRadius: 20,
                      cursor: "pointer",
                    }}
                  />
                </span>
                <div className="allCreateMyLession">
                  <label>Grade level:</label>
                  <FormControl fullWidth>
                    <Select
                      className={classes.select}
                      value={gradeLevel}
                      onChange={handleGradeLevelChange}
                      displayEmpty
                      open={openDropDown?.gradeLevel}
                      onOpen={() => handleMenuOpen("gradeLevel")}
                      onClose={() => handleMenuClose("gradeLevel")}
                      inputProps={{ "aria-label": "Without label" }}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        getContentAnchorEl: null,
                        disableScrollLock: true,
                      }}
                      renderValue={(selected) => {
                        if (!selected) {
                          return <span>Select</span>;
                        }
                        return selected;
                      }}
                    >
                      {gradeLevelData.map((item, index) => (
                        <MenuItem key={index} value={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <div className="allCreateMyLession">
                  <label>State:</label>
                  <FormControl fullWidth>
                    <Select
                      className={classes.select}
                      value={state}
                      onChange={handleStateChange}
                      displayEmpty
                      inputProps={{ "aria-label": "Without label" }}
                      open={openDropDown?.state}
                      onOpen={() => handleMenuOpen("state")}
                      onClose={() => handleMenuClose("state")}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        getContentAnchorEl: null,
                        disableScrollLock: true,
                      }}
                    >
                      <MenuItem value="" hidden>
                        Select
                      </MenuItem>
                      {map(US_STATE, (tz) => (
                        <MenuItem key={tz.code} value={tz.name}>
                          {tz.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <div className="allCreateMyLession">
                  <label>Time frame per lesson:</label>
                  <FormControl fullWidth>
                    <Select
                      className={classes.select}
                      value={timeFrame}
                      onChange={(e) => {
                        handleTimeFrameChange(e);
                      }}
                      open={openDropDown?.timeFrame}
                      onOpen={() => handleMenuOpen("timeFrame")}
                      onClose={() => {
                        handleMenuClose("timeFrame");
                      }}
                      displayEmpty
                      inputProps={{ "aria-label": "Without label" }}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        getContentAnchorEl: null,
                        disableScrollLock: true,
                      }}
                      renderValue={(selected) => {
                        if (!selected) {
                          return <span>Select</span>;
                        }
                        return selected;
                      }}
                    >
                      {map(timeMenuItems, (item, index) => {
                        if (item.label === "custom") {
                          return (
                            <>
                              {!showCustomTime ? (
                                <Button
                                  className="superLessonCustomTime"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleCustomTimeClick();
                                    handleMenuOpen("timeFrame");
                                  }}
                                >
                                  Custom Time
                                </Button>
                              ) : (
                                <div
                                  onClick={(e) => handleMenuOpen("timeFrame")}
                                >
                                  <div className="customTimeContainer">
                                    <div className="superLessonCustomField">
                                      {/* <span>Hours:</span> */}
                                      <div style={{ marginLeft: 10 }}>
                                        <TextField
                                          placeholder="hr"
                                          id="weightage"
                                          variant="outlined"
                                          type="number"
                                          value={hours}
                                          onChange={handleHoursChange}
                                          inputProps={{
                                            min: 0,
                                            max: 2,
                                          }}
                                          onKeyDown={(e) => e.stopPropagation()}
                                        />
                                      </div>
                                      <div style={{ marginLeft: 10 }}>
                                        <TextField
                                          placeholder="min"
                                          id="weightage"
                                          variant="outlined"
                                          type="number"
                                          value={minutes}
                                          onChange={handleMinutesChange}
                                          inputProps={{
                                            min: 0,
                                            max: 59,
                                          }}
                                          onKeyDown={(e) => e.stopPropagation()}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  {minuteError && (
                                    <p
                                      style={{
                                        color: "red",
                                        textAlign: "center",
                                        width: "155px",
                                        margin: "0 auto",
                                      }}
                                    >
                                      {minuteError}
                                    </p>
                                  )}
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "center",
                                      gap: "10px",
                                    }}
                                  >
                                    <Button
                                      className="superLessonCustomTime superLessonCustomBtn"
                                      onClick={(e) => {
                                        onCustomTime();
                                        e.stopPropagation();
                                      }}
                                    >
                                      Ok
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        }
                        return (
                          <MenuItem key={index} value={item?.value}>
                            {item.label}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </div>
                <div className="allCreateMyLession">
                  <label>Days:</label>
                  <FormControl fullWidth>
                    <Select
                      className={classes.select}
                      value={days}
                      onChange={handleDaysChange}
                      displayEmpty
                      inputProps={{ "aria-label": "Without label" }}
                      open={openDropDown?.days}
                      onOpen={() => handleMenuOpen("days")}
                      onClose={() => handleMenuClose("days")}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        getContentAnchorEl: null,
                        disableScrollLock: true,
                      }}
                    >
                      <MenuItem value="" hidden>
                        {" "}
                        Select
                      </MenuItem>
                      <MenuItem value={1}>1</MenuItem>
                      <MenuItem value={2}>2</MenuItem>
                      <MenuItem value={3}>3</MenuItem>
                      <MenuItem value={4}>4</MenuItem>
                      <MenuItem value={5}>5</MenuItem>
                    </Select>
                  </FormControl>
                </div>
                    <FieldContainer className="allCreateMyLession">
                      <label>
                        Select Page Content
                      </label>
                      <TwoFiledContainer style={{ width: "79%" }} width={"50%"}>
                        <FormControl fullWidth>
                            <Select
                            input={<Input disableUnderline />}
                            className={classes.select}
                            value={selectedFromPage}
                            onChange={handleFromPageChange}
                            displayEmpty
                            inputProps={{ "aria-label": "Without label" }}
                            open={openDropDown?.from_page}
                            onOpen={() => handleMenuOpen("from_page")}
                            onClose={() => handleMenuClose("from_page")}
                            MenuProps={{
                              anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "left",
                              },
                              transformOrigin: {
                                vertical: "top",
                                horizontal: "left",
                              },
                              getContentAnchorEl: null,
                              disableScrollLock: true,
                            }}
                          >
                            {map(selectableContent, (bk, index) => (
                              <MenuItem key={index} value={bk.numberMasked}>
                                {is_balcon
                                  ? bk.page_dynamic_id
                                  : bk.numberMasked}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <Select
                            input={<Input disableUnderline />}
                            className={classes.select}
                            value={selectedToPage}
                            disabled={!selectedFromPage}
                            onChange={handleToPageChange}
                            displayEmpty
                            inputProps={{ "aria-label": "Without label" }}
                            open={openDropDown?.to_page}
                            onOpen={() => handleMenuOpen("to_page")}
                            onClose={() => handleMenuClose("to_page")}
                            MenuProps={{
                              anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "left",
                              },
                              transformOrigin: {
                                vertical: "top",
                                horizontal: "left",
                              },
                              getContentAnchorEl: null,
                              disableScrollLock: true,
                            }}
                              // renderValue={(selected) => {
                              //   if (!selected) {
                              //     {console.log('displayDefaultToPage', displayDefaultToPage)}
                              //     return <span>{displayDefaultToPage}</span>;
                              //   }
                              // }}
                          >
                            {map(
                              selectableContent.slice(fromPageIndex),
                              (bk, index) => (
                                <MenuItem key={index} value={bk.numberMasked}>
                                  {is_balcon
                                    ? bk.page_dynamic_id
                                    : bk.numberMasked}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>

                      </TwoFiledContainer>
                    </FieldContainer>
                <div className="allCreateMyLession actSelectCstm">
                  <label>Standard Selection:</label>
                  <FormControl fullWidth className="actSelectForm">
                    <Select
                      multiple
                      className={classes.select}
                      value={standard}
                      onChange={handleStandardChange}
                      displayEmpty
                      open={openDropDown?.standard}
                      onOpen={() => handleMenuOpen("standard")}
                      onClose={() => handleMenuClose("standard")}
                      inputProps={{ "aria-label": "Without label" }}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        PaperProps: {
                          style: {
                            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                            width: 250,
                            overflowX: 'auto',
                          },
                        },
                        getContentAnchorEl: null,
                        disableScrollLock: true,
                      }}
                      renderValue={() => renderSelectedValues(standard)}
                    >
                      {standardSelection.map((item, index) => (
                        <MenuItem key={index} value={item?.label}>
                          <Checkbox
                            checked={standard.indexOf(item?.label) > -1}
                          />
                          <ListItemText 
                            primary={item?.label}
                            primaryTypographyProps={{
                              style: {
                                whiteSpace: 'nowrap',
                                overflow: 'visible',
                              }
                            }}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <div className="allCreateMyLession actSelectCstm">
                  <label>Activities:</label>
                  <FormControl fullWidth className="actSelectForm">
                    <Select
                      multiple
                      className={classes.select}
                      value={activities}
                      onChange={handleActivitiesChange}
                      displayEmpty
                      open={openDropDown?.activities}
                      onOpen={() => handleMenuOpen("activities")}
                      onClose={() => handleMenuClose("activities")}
                      inputProps={{ "aria-label": "Without label" }}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        PaperProps: {
                          style: {
                            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                            width: 250,
                          },
                        },
                        getContentAnchorEl: null,
                        disableScrollLock: true,
                      }}
                      renderValue={() => renderSelectedValues(activities)}
                    >
                      {activitiesSelection.map((item, index) => (
                        <MenuItem key={index} value={item?.label}>
                          <Checkbox
                            checked={activities.indexOf(item?.label) > -1}
                          />
                          <ListItemText primary={item?.label} />
                        </MenuItem>

                        // <MenuItem key={index} value={item?.label}>
                        //   {item?.label}
                        // </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <div className="allCreateMyLession">
                  <label>Language:</label>
                  <FormControl fullWidth>
                    <Select
                      className={classes.select}
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      displayEmpty
                      inputProps={{ "aria-label": "Without label" }}
                      open={openDropDown?.language}
                      onOpen={() => handleMenuOpen("language")}
                      onClose={() => handleMenuClose("language")}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        getContentAnchorEl: null,
                        disableScrollLock: true,
                      }}
                    >
                      <MenuItem value="" hidden>
                        Select
                      </MenuItem>
                      <MenuItem value={"English"}>English</MenuItem>
                      <MenuItem value={"Spanish"}>Spanish</MenuItem>
                      {/* <MenuItem value={"French"}>French</MenuItem> */}
                    </Select>
                  </FormControl>
                </div>
                  <div className="allCreateMyLession actSelectCstm">
                  <label> Proficiency Level:</label>
                    <FormControl fullWidth className="actSelectForm">
                      <Select
                        multiple
                        className={classes.select}
                        value={proficiencyLevel}
                        onChange={(e) => setProficiencyLevel(e.target.value)}
                        displayEmpty
                        open={openDropDown?.profiency}
                        onOpen={() => handleMenuOpen("profiency")}
                        onClose={() => handleMenuClose("profiency")}
                        inputProps={{ "aria-label": "Without label" }}
                        MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        PaperProps: {
                          style: {
                            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                            width: 250,
                            overflowX: 'auto',
                          },
                        },
                        getContentAnchorEl: null,
                        disableScrollLock: true,
                        }}
                        renderValue={() => renderSelectedValues(proficiencyLevel)}
                      >
                        {levelsData.map((item, index) => (
                          <MenuItem key={index} value={item?.label}>
                            <Checkbox
                              checked={proficiencyLevel.indexOf(item?.label) > -1}
                            />
                            <ListItemText 
                              primary={item?.label}
                              primaryTypographyProps={{
                                style: {
                                  whiteSpace: 'nowrap',
                                  overflow: 'visible',
                                }
                              }}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                </div>
                <FieldContainer className="allCreateMyLession super-lesson-own-vocab allCreateMyLessionStory">
                     <label>Add Your Vocab:</label>
                     <FormControl>
                      <StyledAutocomplete
                        multiple
                        freeSolo
                        id="tags-outlined"
                        options={[]}
                        value={currentTokens}
                        inputValue={vocabInput}
                        onInputChange={(event, newInputValue, reason) => {
                          // user typed
                          if (reason === "input") {
                            // if a comma appears anywhere, split & commit, then clear
                            if (newInputValue.includes(",")) {
                              commitTokens(newInputValue.split(","));
                              setVocabInput(""); // clear the textbox
                            } else {
                              setVocabInput(newInputValue);
                            }
                          }
                        }}
                        onKeyDown={(event) => {
                          // Prevent the comma character from being inserted into the field
                          if (event.key === ",") {
                            event.preventDefault();
                            if (vocabInput.trim()) {
                              commitTokens([vocabInput]);
                              setVocabInput(""); // clear after commit
                            }
                          }
                          // Optional: also commit on Enter
                          if (event.key === "Enter") {
                            event.preventDefault();
                            if (vocabInput.trim()) {
                              commitTokens([vocabInput]);
                              setVocabInput("");
                            }
                          }
                        }}
                        onChange={(event, value) => {
                          // chip add/remove via UI => keep string in sync
                          setOwnVocab(value.join(","));
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            padding: 0,
                            border: "none",
                            "& fieldset": {
                              display: "none",
                            },
                          },
                          "& .MuiInputBase-root::before, & .MuiInputBase-root::after": {
                            display: "none !important",
                          },
                          "& .MuiAutocomplete-listbox": {
                            display: "flex",
                            flexDirection: "row",
                            overflowX: "auto",
                            maxWidth: "100%",
                            whiteSpace: "nowrap",
                          },
                          // "& .MuiAutocomplete-tag": {
                          //   marginRight: "5px",
                          //   marginBottom: "0",
                          // },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            variant="standard"
                            InputProps={{
                              ...params.InputProps,
                              disableUnderline: true,
                              sx: {
                                border: "none",
                              },
                            }}
                            InputLabelProps={{ shrink: true }}
                            type="text"
                            placeholder="Optional."
                          />
                        )}
                      />
                     </FormControl>


                </FieldContainer>
                <div className="myLessionBtn create-lesson-button">
                  <Button
                    style={{
                      opacity:
                        loader ||
                        !(
                          gradeLevel &&
                          state &&
                          standard.length > 0 &&
                          timeFrame &&
                          days &&
                          language
                        )
                          ? 0.6
                          : 1,
                    }}
                    onClick={handleCreateLesson}
                    disabled={
                      loader ||
                      !(
                        gradeLevel &&
                        state &&
                        standard.length > 0 &&
                        timeFrame &&
                        days &&
                        language
                      )
                    }
                  >
                    {loader && <Loading size={17} />} Create my lesson
                  </Button>
                </div>
                <div className="createdTopicContnt createdTopicContntCstm">
                  <span>
                    Note: The lesson will be created using the topic and the
                    content covered in the lesson that includes this page.
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </SentryWrap>
  );
};

export default CreateLessonModal;
